export type CompanyRouteGroupKey =
  | 'operations'
  | 'fleet-management'
  | 'accountancy'
  | 'master-data'
  | 'people-access'
  | 'reports'
  | 'settings'
  | 'superuser';

type QueryValue = string | number | boolean | null | undefined;

const normalizeSegment = (segment: string | number) =>
  String(segment).replace(/^\/+|\/+$/g, '');

const buildPath = (...segments: Array<string | number | null | undefined>) =>
  `/${segments
    .filter((segment): segment is string | number => segment !== null && segment !== undefined)
    .map(normalizeSegment)
    .filter(Boolean)
    .join('/')}`;

export const APP_ROUTE_SEGMENTS = {
  base: 'rentman',
  login: 'login',
  register: 'register',
  vehicles: 'vehicles',
  reservationConfirmation: 'reservation-confirmation',
  dashboard: 'dashboard',
  profile: 'profile',
  reservations: 'reservations',
  companyDashboard: 'company',
} as const;

export const COMPANY_ROUTE_GROUP_SEGMENTS: Record<CompanyRouteGroupKey, string> = {
  operations: 'operations',
  'fleet-management': 'fleet-management',
  accountancy: 'accountancy',
  'master-data': 'master-data',
  'people-access': 'people-access',
  reports: 'reports',
  settings: 'settings',
  superuser: 'superuser',
};

export const buildCompanyGroupRoutePath = (
  groupId: CompanyRouteGroupKey,
  routeSegment: string
) => buildPath(APP_ROUTE_SEGMENTS.base, COMPANY_ROUTE_GROUP_SEGMENTS[groupId], routeSegment);

export const APP_ROUTES = {
  root: '/',
  rentman: buildPath(APP_ROUTE_SEGMENTS.base),
  auth: {
    login: buildPath(APP_ROUTE_SEGMENTS.base, APP_ROUTE_SEGMENTS.login),
    register: buildPath(APP_ROUTE_SEGMENTS.base, APP_ROUTE_SEGMENTS.register),
  },
  public: {
    // The public search/booking page now lives at the site root (no /rentman/search route).
    search: '/',
    vehicleDetails: (vehicleId: string | number = ':id') =>
      buildPath(APP_ROUTE_SEGMENTS.base, APP_ROUTE_SEGMENTS.vehicles, vehicleId),
    reservationConfirmation: (reservationId: string | number = ':id') =>
      buildPath(
        APP_ROUTE_SEGMENTS.base,
        APP_ROUTE_SEGMENTS.reservationConfirmation,
        reservationId
      ),
  },
  user: {
    dashboard: buildPath(APP_ROUTE_SEGMENTS.base, APP_ROUTE_SEGMENTS.dashboard),
    profile: buildPath(APP_ROUTE_SEGMENTS.base, APP_ROUTE_SEGMENTS.profile),
    reservations: buildPath(APP_ROUTE_SEGMENTS.base, APP_ROUTE_SEGMENTS.reservations),
  },
  company: {
    dashboard: buildPath(APP_ROUTE_SEGMENTS.base, APP_ROUTE_SEGMENTS.companyDashboard),
    group: buildCompanyGroupRoutePath,
  },
} as const;

export const withSearch = (
  path: string,
  params: URLSearchParams | Record<string, QueryValue>
) => {
  const searchParams =
    params instanceof URLSearchParams
      ? params
      : Object.entries(params).reduce((accumulator, [key, value]) => {
          if (value !== undefined && value !== null && String(value) !== '') {
            accumulator.set(key, String(value));
          }
          return accumulator;
        }, new URLSearchParams());

  const queryString = searchParams.toString();
  return queryString ? `${path}?${queryString}` : path;
};

export const isAuthPagePath = (pathname: string) =>
  pathname === APP_ROUTES.auth.login ||
  pathname === APP_ROUTES.auth.register;
